import React, { useEffect, useMemo, useState } from "react";
import styled, { keyframes } from "styled-components";
import { Modal, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import { Notify } from "notiflix";
import { getAllPrizes } from "../../../../../data/api/prizes/PrizeRoutes";
import validErrors from "../../../../../hooks/validErrors";

/**
 * AwardsModal
 * - UI responsive: NO se desborda
 * - Header y footer fijos
 * - Body con scroll interno cuando haga falta
 *
 * Data:
 * getAllPrizes() => { success: true, data: [{id,hits,prize,compensation,updated_at}, ...] }
 * - compensation == 1 => COP
 * - compensation != 1 => Jugadas
 */
const AwardsModal = ({ open, onClose }) => {
  const [load, setLoad] = useState(false);
  const [prizes, setPrizes] = useState([]);

  const loadData = async () => {
    setLoad(true);
    try {
      const { data } = await getAllPrizes();
      if (data?.success) {
        setPrizes(Array.isArray(data?.data) ? data.data : []);
      } else {
        setPrizes([]);
        if (data?.message) Notify.failure(data.message);
      }
    } catch (error) {
      validErrors(error);
    } finally {
      setLoad(false);
    }
  };

  useEffect(() => {
    if (open) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const sortedPrizes = useMemo(() => {
    // normalmente vienen 1..6 o 6..1; lo ordenamos por hits desc para mantener el diseño
    return [...prizes].sort((a, b) => (b?.hits ?? 0) - (a?.hits ?? 0));
  }, [prizes]);

  const getRowClassByHits = (hits) => {
    // mapeo visual según tu mock
    switch (Number(hits)) {
      case 6:
        return "gold";
      case 5:
        return "orange";
      case 4:
        return "red";
      case 3:
        return "blue";
      case 2:
        return "green";
      case 1:
        return "teal";
      default:
        return "blue";
    }
  };

  const formatCOP = (value) => {
    const num = Number(value ?? 0);
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      maximumFractionDigits: 0,
    }).format(num);
  };

  const formatPlays = (value) => {
    const num = Number(value ?? 0);
    return `${new Intl.NumberFormat("es-CO", {
      maximumFractionDigits: 0,
    }).format(num)} Jugadas`;
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Center>
        <Card>
          {/* Header fijo */}
          <Header>
            <TitleWrap>
              <EmojiEventsIcon className="trophy" />
              <Typography className="title">
                Recompensas por Aciertos
              </Typography>
            </TitleWrap>

            <IconButton onClick={onClose} className="closeBtn" size="small">
              <CloseIcon />
            </IconButton>
          </Header>

          {/* Body scrollable */}
          <Body>
            {load && (
              <LoaderWrap>
                <Spinner />
                <Typography className="loadingText">
                  Cargando premios...
                </Typography>
              </LoaderWrap>
            )}

            <Content dim={load}>
              {sortedPrizes?.length === 0 ? (
                <EmptyState>
                  <Typography className="emptyTitle">
                    Sin información
                  </Typography>
                  <Typography className="emptyText">
                    No se encontraron premios para mostrar.
                  </Typography>
                </EmptyState>
              ) : (
                sortedPrizes.map((item) => {
                  const hits = Number(item?.hits ?? 0);
                  const compensation = Number(item?.compensation ?? 0); // 1 => COP
                  const prize = item?.prize ?? 0;

                  return (
                    <AwardRow
                      key={item?.id}
                      className={getRowClassByHits(hits)}
                    >
                      <span className="left">
                        {hits} {hits === 1 ? "Acierto" : "Aciertos"}
                      </span>

                      <span className="right">
                        {compensation === 1 ? (
                          formatCOP(prize)
                        ) : (
                          <>
                            <ConfirmationNumberIcon className="ticket" />
                            {formatPlays(prize)}
                          </>
                        )}
                      </span>
                    </AwardRow>
                  );
                })
              )}
            </Content>
          </Body>

          {/* Footer fijo */}
          <FooterNote>*Recompensas en el orden de su jugada</FooterNote>
        </Card>
      </Center>
    </Modal>
  );
};

export default AwardsModal;

/* ===================== styled-components ===================== */

const Center = styled.div`
  position: fixed;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 16px;
`;

const Card = styled.div`
  width: min(720px, 40vw);
  max-width: 92vw;

  max-height: min(82vh, 760px);
  overflow: hidden;

  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 22px 55px rgba(0, 0, 0, 0.45);

  display: flex;
  flex-direction: column;

  @media (max-width: 900px) {
    width: min(720px, 92vw);
  }
`;

const Header = styled.div`
  padding: 18px 18px 14px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;

  border-bottom: 1px solid rgba(0, 0, 0, 0.08);

  .closeBtn {
    border: 2px solid rgba(0, 0, 0, 0.25);
    border-radius: 10px;
  }

  @media (max-width: 600px) {
    padding: 16px 14px 12px;
  }
`;

const TitleWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  .trophy {
    font-size: clamp(26px, 2.4vw, 34px);
    color: #f2c94c;
    flex: 0 0 auto;
  }

  .title {
    font-weight: 900;
    font-size: clamp(1.25rem, 2.4vw, 2.05rem);
    line-height: 1.05;
  }
`;

const Body = styled.div`
  padding: 14px 18px;
  flex: 1;
  overflow-y: auto;

  display: grid;
  gap: 12px;

  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.25) transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.25);
    border-radius: 999px;
  }

  @media (max-width: 600px) {
    padding: 12px 14px;
    gap: 10px;
  }
`;

/* ===== loader ===== */
const LoaderWrap = styled.div`
  display: grid;
  place-items: center;
  gap: 10px;
  padding: 6px 0 2px;

  .loadingText {
    color: rgba(26, 29, 40, 0.7);
    font-weight: 800;
    font-size: 0.95rem;
  }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 999px;
  border: 4px solid rgba(116, 63, 227, 0.25);
  border-top-color: #743fe3;
  animation: ${spin} 0.8s linear infinite;
`;

const Content = styled.div`
  display: grid;
  gap: 12px;
  opacity: ${(p) => (p.dim ? 0.6 : 1)};
  pointer-events: ${(p) => (p.dim ? "none" : "auto")};
`;

const EmptyState = styled.div`
  min-height: 220px;
  display: grid;
  place-items: center;
  gap: 10px;
  text-align: center;

  .emptyTitle {
    font-weight: 900;
    color: #1a1d28;
    font-size: 1.2rem;
  }

  .emptyText {
    color: rgba(26, 29, 40, 0.7);
    font-weight: 600;
    max-width: 520px;
    font-size: 0.98rem;
  }
`;

const AwardRow = styled.div`
  min-height: clamp(54px, 6vw, 78px);
  border-radius: 14px;
  padding: clamp(10px, 2vw, 18px);

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;

  color: #fff;
  box-shadow: 0 14px 24px rgba(0, 0, 0, 0.12);

  .left {
    font-weight: 900;
    font-size: clamp(1rem, 1.8vw, 1.55rem);
    white-space: nowrap;
  }

  .right {
    font-weight: 900;
    font-size: clamp(1.05rem, 2.1vw, 1.9rem);
    display: inline-flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;

    .ticket {
      font-size: clamp(20px, 2.2vw, 28px);
      opacity: 0.95;
    }
  }

  &.gold {
    background: linear-gradient(90deg, #f2c94c 0%, #c79a10 100%);
  }
  &.orange {
    background: linear-gradient(90deg, #ff9f43 0%, #ef6c00 100%);
  }
  &.red {
    background: linear-gradient(90deg, #ff6b6b 0%, #d32f2f 100%);
  }
  &.blue {
    background: linear-gradient(90deg, #63a4ff 0%, #2f6cff 100%);
  }
  &.green {
    background: linear-gradient(90deg, #52d67a 0%, #1db954 100%);
  }
  &.teal {
    background: linear-gradient(90deg, #42d9c8 0%, #14b8a6 100%);
  }
`;

const FooterNote = styled.div`
  padding: 12px 18px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);

  text-align: center;
  color: rgba(0, 0, 0, 0.62);
  font-weight: 700;
  font-size: clamp(0.85rem, 1.4vw, 1rem);

  @media (max-width: 600px) {
    padding: 10px 14px;
  }
`;
